import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Segment, Header, Dimmer, Loader, Icon } from 'semantic-ui-react';

const getLogTimestamp = (log) => {
    // Check if log already has a timestamp
    if (log.match(/^\[\d{1,2}:\d{2}:\d{2}\s?[AP]?[M]?\]/)) {
        return log;
    }

    // If log doesn't have a timestamp, add one with the current time
    const timestamp = new Date().toLocaleTimeString();
    return `[${timestamp}] ${log}`;
};

const detectErrorInLogs = (logsArray = []) => {
    if (!logsArray.length) return false;

    return logsArray.some((log) => {
        const text = (log || '').toString();
        const lower = text.toLowerCase();

        if (text.includes('❌ Error:') || text.includes('FAILED-')) {
            return true;
        }
        if (/^❌/.test(text.trim())) {
            return true;
        }
        if (lower.includes('request status updated to failed')) {
            return true;
        }
        if (lower.includes('decommission was not recorded as approved')) {
            return true;
        }
        if (lower.includes('failed with http')) {
            return true;
        }
        if (text.includes('[DEPROVISION_API]') && text.includes('⚠️')) {
            return true;
        }
        return false;
    });
};

const CreationLogsModal = ({
    open,
    onClose,
    logs,
    onCancel,
    isCancelled = false,
    processLabel = 'Provisioning',
    operationFailed = false
}) => {
    const [isComplete, setIsComplete] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isAutoscroll, setIsAutoscroll] = useState(true);
    const [storageCredentials, setStorageCredentials] = useState(null);
    const [processedLogs, setProcessedLogs] = useState([]);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const logsEndRef = useRef(null);
    const logsContainerRef = useRef(null);
    const prevOpenRef = useRef(false);
    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const hasStartedRef = useRef(false);
    const finalElapsedTimeRef = useRef(null); // Preserve final elapsed time across reloads

    // Helper function to check if logs indicate completion (stream ended)
    const checkLogsForCompletion = (logsArray) => {
        if (!logsArray || logsArray.length === 0) return false;
        const lastLog = logsArray[logsArray.length - 1];
        // Check for stream end message (works for both success and failure)
        return lastLog.includes('✅ Notification emails sent') ||
            lastLog.includes('✅ Error logs also saved to file:') ||
            lastLog.includes('✅ Logs also saved to file:') ||
            lastLog.includes('Operation completed. Ending stream.');
    };

    // Helper to parse timestamp from log (for fallback calculation)
    const parseLogTimestamp = (log) => {
        if (!log) return null;
        const timePattern24 = /\[(\d{1,2}):(\d{2}):(\d{2})\]/;
        const timePattern12 = /\[(\d{1,2}):(\d{2}):(\d{2})\s+([AP]M)\]/i;
        
        let match = log.match(timePattern12);
        if (match) {
            let hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const seconds = parseInt(match[3], 10);
            const ampm = match[4].toUpperCase();
            if (ampm === 'PM' && hours !== 12) hours += 12;
            if (ampm === 'AM' && hours === 12) hours = 0;
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, seconds);
        }
        
        match = log.match(timePattern24);
        if (match) {
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            const seconds = parseInt(match[3], 10);
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, seconds);
        }
        return null;
    };

    // Reset all internal state when modal opens (new tenant creation starts)
    useEffect(() => {
        // If modal was closed and is now opening
        if (open && !prevOpenRef.current) {
            // Check if logs indicate this is a completed process (reload scenario)
            const isAlreadyCompleted = checkLogsForCompletion(logs);
            
            if (isAlreadyCompleted) {
                // Restore preserved timer value if available
                if (finalElapsedTimeRef.current !== null) {
                    setElapsedSeconds(finalElapsedTimeRef.current);
                    setIsComplete(true);
                    // Check for errors in logs
                    const hasErrorInLogs = detectErrorInLogs(logs) || operationFailed;
                    setHasError(hasErrorInLogs);
                } else {
                    // Will be calculated from log timestamps in the logs processing effect
                    setIsComplete(true);
                    // Check for errors in logs
                    const hasErrorInLogs = detectErrorInLogs(logs) || operationFailed;
                    setHasError(hasErrorInLogs);
                }
            } else {
                // New process - reset all state and start timer immediately
                setIsComplete(false);
                setHasError(false);
                setStorageCredentials(null);
                setElapsedSeconds(0);
                setIsAutoscroll(true);
                setShowCancelConfirmation(false);
                // Start timer immediately when modal opens for new process
                // This ensures timer starts even if provisioning fails immediately
                startTimeRef.current = new Date();
                endTimeRef.current = null;
                hasStartedRef.current = true;
                finalElapsedTimeRef.current = null;
                // Clear any existing timer
                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                }
            }
        }
        if (!open) {
            // Clean up when modal closes (but preserve final elapsed time)
            // Don't clear finalElapsedTimeRef - we want to preserve it
            startTimeRef.current = null;
            endTimeRef.current = null;
            hasStartedRef.current = false;
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        }
        prevOpenRef.current = open;
    }, [open, logs, operationFailed]);

    // Process logs to ensure each has a unique timestamp
    useEffect(() => {
        // Reprocess all logs whenever logs array changes
        // This handles both new logs being added and logs being reset/repopulated
        const newProcessedLogs = logs.map(log => getLogTimestamp(log));
        setProcessedLogs(newProcessedLogs);
        
        // If modal is open and logs show completion but we don't have preserved time, calculate it from timestamps
        if (open && logs.length > 0 && checkLogsForCompletion(logs) && finalElapsedTimeRef.current === null) {
            const firstLog = newProcessedLogs[0];
            const lastLog = newProcessedLogs[newProcessedLogs.length - 1];
            const startTime = parseLogTimestamp(firstLog);
            const endTime = parseLogTimestamp(lastLog);
            if (startTime && endTime) {
                const calculatedDuration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
                if (calculatedDuration > 0) {
                    setElapsedSeconds(calculatedDuration);
                    finalElapsedTimeRef.current = calculatedDuration;
                    setIsComplete(true);
                }
            }
        }
    }, [logs, open, isComplete]);

    const scrollToBottom = () => {
        if (isAutoscroll && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        // Reset completion state if logs are empty (new creation starting)
        // But don't reset timer if we've already started tracking (modal is open)
        if (logs.length === 0) {
            setIsComplete(false);
            setHasError(false);
            setStorageCredentials(null);
            // Don't reset timer if modal is open and we've already started tracking
            // This prevents timer reset when logs are cleared temporarily
            if (open && hasStartedRef.current) {
                return;
            }
            // Reset start tracking only if modal is closed or hasn't started yet
            hasStartedRef.current = false;
            startTimeRef.current = null;
            endTimeRef.current = null;
            return;
        }

        // Parse logs for storage account details and check completion / failure
        const lastLog = logs[logs.length - 1];
        
        // Check for storage credentials
        if (lastLog?.includes('Azure Blob Storage credentials:')) {
            try {
                const credentialsJson = lastLog.split('Azure Blob Storage credentials:')[1];
                const credentials = JSON.parse(credentialsJson);
                setStorageCredentials(credentials);
            } catch (error) {
                console.error('Error parsing storage credentials:', error);
            }
        }

        // Check for completion messages (stream ended)
        const isCompleted =
            lastLog.includes('✅ Notification emails sent') ||
            lastLog.includes('✅ Error logs also saved to file:') ||
            lastLog.includes('✅ Logs also saved to file:') ||
            lastLog.includes('Operation completed. Ending stream.');

        // Check if the overall operation failed (any error markers in the logs)
        const hasErrorInLogs = detectErrorInLogs(logs) || operationFailed;

        // If process just completed (success or failure), store end time and stop timer
        if (isCompleted && !isComplete && !endTimeRef.current) {
            // Ensure timer started - if not, start it now (shouldn't happen but safety check)
            if (!startTimeRef.current && open) {
                startTimeRef.current = new Date();
                hasStartedRef.current = true;
            }
            
            if (startTimeRef.current) {
                endTimeRef.current = new Date();
                // Stop the timer interval
                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                }
                // Calculate final duration and preserve it (for both success and failure)
                const finalDuration = Math.floor((endTimeRef.current.getTime() - startTimeRef.current.getTime()) / 1000);
                setElapsedSeconds(finalDuration);
                finalElapsedTimeRef.current = finalDuration; // Preserve for reload scenarios
            } else {
                // If timer never started, try to calculate from log timestamps
                const processedLogsForCalc = logs.map(log => getLogTimestamp(log));
                const firstLog = processedLogsForCalc[0];
                const lastLog = processedLogsForCalc[processedLogsForCalc.length - 1];
                const startTime = parseLogTimestamp(firstLog);
                const endTime = parseLogTimestamp(lastLog);
                if (startTime && endTime) {
                    const calculatedDuration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
                    if (calculatedDuration > 0) {
                        setElapsedSeconds(calculatedDuration);
                        finalElapsedTimeRef.current = calculatedDuration;
                    }
                }
            }
        }

        // If already completed, ensure timer is preserved
        if (isCompleted) {
            // If we have preserved time, restore it if needed
            if (finalElapsedTimeRef.current !== null) {
                // Only restore if elapsedSeconds is 0 or significantly different (indicating a reset)
                if (elapsedSeconds === 0 || Math.abs(elapsedSeconds - finalElapsedTimeRef.current) > 10) {
                    setElapsedSeconds(finalElapsedTimeRef.current);
                }
            } 
            // If no preserved time but we have current elapsed time and start time, preserve it
            else if (elapsedSeconds > 0 && startTimeRef.current) {
                finalElapsedTimeRef.current = elapsedSeconds;
            }
            // If no preserved time and no current time, try to calculate from timestamps
            else if (finalElapsedTimeRef.current === null && logs.length > 0) {
                const processedLogsForCalc = logs.map(log => getLogTimestamp(log));
                const firstLog = processedLogsForCalc[0];
                const lastLog = processedLogsForCalc[processedLogsForCalc.length - 1];
                const startTime = parseLogTimestamp(firstLog);
                const endTime = parseLogTimestamp(lastLog);
                if (startTime && endTime) {
                    const calculatedDuration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
                    if (calculatedDuration > 0) {
                        setElapsedSeconds(calculatedDuration);
                        finalElapsedTimeRef.current = calculatedDuration;
                    }
                }
            }
        }

        setIsComplete(isCompleted);
        setHasError(hasErrorInLogs);

        scrollToBottom();
    }, [logs, open, isComplete, operationFailed]);

    // Handle scroll events to toggle autoscroll
    const handleScroll = () => {
        if (!logsContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
        // If user scrolls up more than 100px from bottom, disable autoscroll
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
        setIsAutoscroll(isAtBottom);
    };

    const getLogStyle = (log) => {
        if (log.includes('🔴') || log.includes('❌') || log.includes('Error')) {
            return { color: '#dc3545', fontWeight: 'bold' };
        }
        if (log.includes('✅')) {
            return { color: '#28a745', fontWeight: 'bold' };
        }
        if (log.includes('⚠️')) {
            return { color: '#ffc107', fontWeight: 'bold' };
        }
        return { color: '#212529' };
    };

    const formatDuration = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Handle cancellation - stop timer and store end time
    useEffect(() => {
        if (isCancelled && startTimeRef.current && !endTimeRef.current) {
            endTimeRef.current = new Date();
            // Stop the timer interval
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
            // Calculate final duration and preserve it
            const finalDuration = Math.floor((endTimeRef.current.getTime() - startTimeRef.current.getTime()) / 1000);
            setElapsedSeconds(finalDuration);
            finalElapsedTimeRef.current = finalDuration; // Preserve for reload scenarios
        }
    }, [isCancelled]);

    // Update elapsed time live while process is running
    useEffect(() => {
        if (!open || !startTimeRef.current) {
            return;
        }

        // If process is complete or cancelled, don't update timer (it's already stopped)
        if (isComplete || isCancelled || endTimeRef.current) {
            return;
        }

        // Calculate elapsed time from start time to now
        const calculateElapsedTime = () => {
            if (!startTimeRef.current) return 0;
            const now = new Date();
            const diffMs = now.getTime() - startTimeRef.current.getTime();
            const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
            return diffSeconds;
        };

        // Update immediately
        const currentTime = calculateElapsedTime();
        setElapsedSeconds(currentTime);

        // Set up interval to update every second
        if (!timerIntervalRef.current) {
            timerIntervalRef.current = setInterval(() => {
                const updatedTime = calculateElapsedTime();
                setElapsedSeconds(updatedTime);
            }, 1000);
        }

        // Cleanup interval when component unmounts or process ends
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, [open, isComplete, isCancelled]);

    const handleCancelClick = () => {
        setShowCancelConfirmation(true);
    };

    const handleConfirmCancel = () => {
        setShowCancelConfirmation(false);
        if (onCancel) {
            onCancel();
        }
    };

    const handleCancelConfirmation = () => {
        setShowCancelConfirmation(false);
    };

    return (
        <Modal
            open={open}
            onClose={() => isComplete && onClose()}
            size="large"
            className="logs-modal"
        >
            <Modal.Header>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div>
                            {isCancelled
                                ? `${processLabel} cancelled`
                                : isComplete && hasError
                                ? `${processLabel} failed`
                                : isComplete
                                ? `${processLabel} completed`
                                : `${processLabel} is in progress...`}
                        </div>
                        {open && (startTimeRef.current || finalElapsedTimeRef.current !== null) && (
                            <div style={{ marginTop: '2px', fontSize: '0.6em', color: '#aaa', lineHeight: '1.1' }}>
                                {isComplete || isCancelled
                                    ? `Total time: ${formatDuration(finalElapsedTimeRef.current !== null ? finalElapsedTimeRef.current : elapsedSeconds)}`
                                    : `Time elapsed: ${formatDuration(elapsedSeconds)}`}
                            </div>
                        )}
                    </div>
                    {!isComplete && !isCancelled && hasStartedRef.current && (
                        <div>
                            <Icon name="circle" color="green" size="small" className="blinking" /> Live
                        </div>
                    )}
                    {isComplete && !hasError && (
                        <div style={{ color: '#28a745' }}>
                            <Icon name="check circle" color="green" size="small" /> Completed
                        </div>
                    )}
                    {isComplete && hasError && (
                        <div style={{ color: '#dc3545' }}>
                            <Icon name="times circle" color="red" size="small" /> Failed
                        </div>
                    )}
                    {isCancelled && (
                        <div style={{ color: '#dc3545' }}>
                            <Icon name="times circle" color="red" size="small" /> Cancelled
                        </div>
                    )}
                </div>
            </Modal.Header>
            <Modal.Content>
                <div style={{
                    position: 'relative',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    background: '#f5f5f5',
                    padding: '15px',
                    borderRadius: '4px'
                }}
                    ref={logsContainerRef}
                    onScroll={handleScroll}>
                    {logs.length === 0 && !isComplete && (
                        <Dimmer active>
                            <Loader>Loading</Loader>
                        </Dimmer>
                    )}

                    {processedLogs.map((log, index) => (
                        <div
                            key={index}
                            style={{
                                ...getLogStyle(log),
                                marginBottom: '8px',
                                padding: '4px 0',
                                borderBottom: '1px solid #eee'
                            }}
                        >
                            {log}
                        </div>
                    ))}
                    <div ref={logsEndRef} />
                </div>

                {storageCredentials && (
                    <Segment style={{ marginTop: '20px' }}>
                        <Header as="h4">Azure Blob Storage Credentials</Header>
                        <div style={{
                            fontFamily: 'monospace',
                            fontSize: '0.9em',
                            background: '#f8f9fa',
                            padding: '15px',
                            borderRadius: '4px',
                            border: '1px solid #e9ecef'
                        }}>
                            <p><strong>Storage Account Name:</strong> {storageCredentials.name}</p>
                            <p><strong>Container Name:</strong> {storageCredentials.containerName}</p>
                            <p><strong>Blob Endpoint:</strong> {storageCredentials.blobEndpoint}</p>
                            <p><strong>Account Key:</strong> {storageCredentials.accountKey}</p>
                            <p style={{ wordBreak: 'break-all' }}>
                                <strong>Connection String:</strong> {storageCredentials.connectionString}
                            </p>
                        </div>
                    </Segment>
                )}
            </Modal.Content>
            <Modal.Actions>
                {!isComplete && !isCancelled && onCancel && (
                    <Button
                        onClick={handleCancelClick}
                        color="red"
                        icon="times"
                        content="Cancel Operation"
                    />
                )}
                <Button
                    onClick={onClose}
                    disabled={!isComplete && !isCancelled}
                    color={isComplete && !hasError ? 'green' : isCancelled ? 'grey' : 'grey'}
                >
                    {isComplete
                        ? 'Close'
                        : isCancelled
                            ? 'Close'
                            : 'Creating...'}
                </Button>
            </Modal.Actions>

            <style jsx>{`
                .blinking {
                    animation: blink 1.5s linear infinite;
                }
                @keyframes blink {
                    0% { opacity: 1; }
                    50% { opacity: 0.3; }
                    100% { opacity: 1; }
                }
            `}</style>

            {/* Cancel Confirmation Dialog */}
            <Modal
                open={showCancelConfirmation}
                onClose={handleCancelConfirmation}
                size="small"
            >
                <Modal.Header>Confirm Cancellation</Modal.Header>
                <Modal.Content>
                    <p>This action will cancel all ongoing operations except VM creation. Manual deletion of VMs will be required to clean up resources in the cloud platform.</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={handleCancelConfirmation}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmCancel} color="red">
                        OK
                    </Button>
                </Modal.Actions>
            </Modal>
        </Modal>
    );
};

export default CreationLogsModal;